import { useMemo, useRef } from 'react';
import styled from 'styled-components/macro';
import classNames from 'classnames';
import { BaseReactComponent } from '../../../types';

import { useIntl } from 'react-intl';
import { useFormControl } from '@mui/material/FormControl';
import {
  IconButton,
  Input,
  InputProps,
  InputAdornment,
  SvgIconProps
} from '@mui/material';
import { Close } from '@mui/icons-material';

export interface HiInputProps extends BaseReactComponent {
  /**
   * The field value.
   */
  value?: InputProps['value'];
  /**
   * Callback fired when the value is changed.
   *
   * @param {string} value The input value.
   * @param {null | React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>} event The event source of the callback (null when reset button click).
   */
  onChange?: (
    value: unknown,
    event: null | React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => void;
  /**
   * Name attribute of the `input` element.
   */
  name?: InputProps['name'];
  /**
   * Type of the input element. It should be [a valid HTML5 input type](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#Form_%3Cinput%3E_types).
   */
  type?: InputProps['type'];
  /**
   * If `true`, the `input` element is required.
   * The prop defaults to the value (`false`) inherited from the parent HiFormControl component.
   */
  required?: InputProps['required'];
  /**
   * If `true`, the component is disabled.
   * The prop defaults to the value (`false`) inherited from the parent HiFormControl component.
   */
  disabled?: InputProps['disabled'];
  /**
   * The short hint displayed in the input before the user enters a value.
   */
  placeholder?: InputProps['placeholder'];
  /**
   * Icon element placed before the input.
   */
  startIcon?: React.ComponentType<SvgIconProps>;
  /**
   * Icon element placed after the input.
   */
  endIcon?: React.ComponentType<SvgIconProps>;
  /**
   * If `true`, display an icon button to clear the field value.
   */
  clearIcon?: boolean;
  /**
   * Pass a ref to the `input` element.
   */
  inputRef?: React.RefObject<HTMLInputElement | null>;
  /**
   * Override Mui Input props
   * (https://mui.com/material-ui/api/input/).
   */
  InputProps?: InputProps;
}

/**
 * HiInput is the component for text fields in Console.
 *
 * It extends Mui's `Input` component.
 *
 * Most of the time, you should encapsulate the HiInput into an `HiFormControl` to add label and help text and manage the state.
 */
const HiInput = ({
  value,
  onChange,
  name,
  type = 'text',
  required,
  disabled,
  placeholder,
  startIcon: StartIconComponent,
  endIcon: EndIconComponent,
  clearIcon = true,
  inputRef,
  InputProps,
  ...other
}: HiInputProps) => {
  const intl = useIntl();
  const formControl = useFormControl();

  // Input references
  const internalInputRef = useRef<HTMLInputElement>();
  const inputReference = useMemo(() => {
    return inputRef || internalInputRef;
  }, [inputRef]);

  // Callback when clear icon is clicked
  const handleReset = () => {
    // Call on change with empty value
    onChange && onChange('', null);
    // Focus the input
    inputReference?.current?.focus();
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    onChange && onChange(event.target.value, event);
  };

  return (
    <StyledInput
      {...other}
      {...InputProps}
      inputRef={inputReference}
      value={value}
      onChange={handleChange}
      name={name}
      type={type}
      required={required}
      disabled={disabled}
      placeholder={placeholder}
      startAdornment={
        (StartIconComponent || InputProps?.startAdornment) && (
          <InputAdornment
            position="start"
            className="HiInput-adornmentContainer"
          >
            <>
              {StartIconComponent ? (
                <StartIconComponent
                  fontSize="small"
                  className="HiInput-adornmentIcon"
                />
              ) : (
                InputProps?.startAdornment
              )}
            </>
          </InputAdornment>
        )
      }
      endAdornment={
        (EndIconComponent || InputProps?.endAdornment || clearIcon) && (
          <InputAdornment
            position="end"
            className={classNames('HiInput-adornmentContainer', {
              'HiInput-endIcon': !!EndIconComponent || InputProps?.endAdornment
            })}
          >
            <>
              {clearIcon && !disabled && !formControl?.disabled && !!value && (
                <IconButton
                  className="HiInput-clearIcon"
                  size="small"
                  onClick={handleReset}
                  title={intl.formatMessage({ id: 'HiInput.clear' })}
                >
                  <Close fontSize="small" />
                </IconButton>
              )}
              {EndIconComponent ? (
                <EndIconComponent
                  fontSize="small"
                  className="HiInput-adornmentIcon"
                />
              ) : (
                InputProps?.endAdornment
              )}
            </>
          </InputAdornment>
        )
      }
      classes={{
        focused: 'HiInput-focused',
        disabled: 'HiInput-disabled',
        error: 'HiInput-error',
        input: 'HiInput-input'
      }}
    />
  );
};

const StyledInput = styled(Input)`
  background-color: ${({ theme }) => theme.palette.input.background};
  ${(props) => props.theme.typography.body2};

  padding: 0 8px;
  padding-bottom: 0 !important;

  &.HiInput-focused {
    background-color: white;
  }

  // Default bottom line
  &:before {
    //border-bottom: 1px solid rgba(32, 39, 43, 0.2) !important;
    border-bottom: ${({ theme }) =>
      `1px solid ${theme.palette.input.bottomLine}`} !important;
  }

  // Bottom line transition and default state
  &:after {
    border-bottom: ${({ theme }) => `1px solid ${theme.palette.primary.main}`};
    border-bottom-width: 1px !important;
    transition: ${({ theme }) =>
      theme.transitions.create('transform', {
        duration: theme.transitions.duration.shorter,
        easing: theme.transitions.easing.easeOut
      })};
    transform: scaleX(0);
    left: 0;
    bottom: 0;
    content: '';
    position: absolute;
    right: 0;
  }

  // On hover, if not in error or disabled, 1px line
  &:hover:not(&.HiInput-error, &.HiInput-disabled):after {
    border-bottom: ${({ theme }) => `1px solid ${theme.palette.primary.main}`};
    transform: scaleX(1);
  }

  // On error, 2px line
  &.HiInput-error:after {
    border-bottom-width: 2px;
    border-bottom: ${({ theme }) =>
      `2px solid ${theme.palette.error.main}`} !important;
  }

  // On focus, 2px line
  &.HiInput-focused:after {
    border-bottom-width: 2px;
    border-bottom: ${({ theme }) =>
      `2px solid ${theme.palette.primary.main}`} !important;
  }

  // When disable, no bottom line
  &.HiInput-disabled {
    background-color: ${({ theme }) => theme.palette.input.disabled};
    &:before,
    &:after {
      border-bottom: none !important;
    }
  }

  .HiInput-input {
    padding: 8px 0 9px !important;
  }

  .HiInput-adornmentContainer {
    pointer-events: none;
    color: ${({ theme }) => theme.palette.text.secondary};

    .HiInput-adornmentIcon {
      color: ${({ theme }) => theme.palette.grey[600]};
    }

    .HiInput-clearIcon {
      visibility: hidden;
      margin-right: 3px;
      pointer-events: all;
    }

    &:not(&.HiInput-endIcon) {
      .HiInput-clearIcon {
        margin-right: -8px;
      }
    }
  }

  &:hover,
  &.HiInput-focused {
    .HiInput-clearIcon {
      visibility: visible;
    }
  }

  // Remove browser default reset icon
  input[type='search']::-webkit-search-decoration,
  input[type='search']::-webkit-search-cancel-button,
  input[type='search']::-webkit-search-results-button,
  input[type='search']::-webkit-search-results-decoration {
    -webkit-appearance: none;
  }
`;

export default HiInput;
